import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  publicationStatusValues,
  SurveySchemaEditorType
} from '../../../constants/types';
import { withLang, langType } from '../../../i18n';
import {
  clearSurveySchemaOptionsAction,
  updateSurveySchemaOptionsAction,
  postSurveySchemaAction,
  addErrorMessageAction,
  setTaskPending,
  setTaskFulfilled,
  putSurveySchemaAction,
  updateSurveySchemaFeedbackIntroduction,
  getOrCheckSurveySchemaCodeAction,
  postNewSurveySchemaVersionAction
} from '../../../actions';
import { getExtraSurveyPart, getSurveySchemaPart } from '../../../api';
import { routes } from '../../../constants';
import ConfirmButton from '../../ConfirmButton';
import editorStyles from '../../Editor/editor.module.css';
import styles from '../preview.module.css';
import PreviewLanguageSelector from '../PreviewLanguageSelector';
import SurveySchemaPreviewTabs from './PreviewTabs';
import { getCompiledPath, setDocumentTitle } from '../../../util';
import withRouter from '../../WithRouter';

const DIMENSIONS_TAB_ROUTE = '/dimensions';

class SchemaPreview extends Component {
  constructor(props) {
    super(props);

    this.onCancel = this.onCancel.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onSchemaStatusChange = this.onSchemaStatusChange.bind(this);
    this.onLanguageSelect = this.onLanguageSelect.bind(this);

    this.state = {
      createNew: !props.params.surveySchemaId,
      submitError: false,
      codeInvalid: false,
      schemaParts: [],
      previewLang: props.lang,
      extraSurveyPart: null
    };
  }

  componentDidMount() {
    const schemaPartIds = this.props.surveySchema.surveySchemaParts.map((part) => part.id);
    const extraPartId = this.props.surveySchema.extraSurveyPart?.id;
    this.props.dispatchSetTaskPending();
    Promise.all([
      Promise.all(schemaPartIds.map((id) => getSurveySchemaPart(id))),
      extraPartId ? getExtraSurveyPart(extraPartId) : null
    ])
      .then(([schemaParts, extraSurveyPart]) => this.setState({ schemaParts, extraSurveyPart }))
      .catch(this.props.dispatchAddErrorMessage)
      .finally(this.props.dispatchSetTaskFulfilled);
  }

  onLanguageSelect(previewLang) {
    this.setState({ previewLang });
  }

  onCancel() {
    this.clearAndReturn();
  }

  async onSave() {
    const {
      surveySchema,
      dispatchPostSurveySchema,
      dispatchPutSurveySchema,
      dispatchUpdateFeedbackIntroduction,
      dispatchPostNewSurveySchemaVersion,
      dispatchCheckCode
    } = this.props;

    const { createNew } = this.state;

    const surveySchemaWithSchemaPartIds = {
      ...surveySchema,
      surveySchemaParts: surveySchema.surveySchemaParts.map((part) => part.id),
      ...(surveySchema.extraSurveyPart && { extraSurveyPart: surveySchema.extraSurveyPart.id })
    };

    try {
      if (createNew) {
        const codeResult = await dispatchCheckCode(surveySchema.code);
        if (!(codeResult.value.available && codeResult.value.valid)) {
          this.setState({ codeInvalid: true });
          return;
        }
      }

      let action;

      if (surveySchema.isNewVersion) {
        action = dispatchPostNewSurveySchemaVersion(surveySchema.id, surveySchemaWithSchemaPartIds);
      } else if (surveySchema.isExistingSchema && surveySchema.useCount > 0) {
        const { feedbackIntroduction } = surveySchema;
        action = dispatchUpdateFeedbackIntroduction(surveySchema.id, { feedbackIntroduction });
      } else if (surveySchema.isExistingSchema) {
        action = dispatchPutSurveySchema(surveySchema.id, surveySchemaWithSchemaPartIds);
      } else {
        action = dispatchPostSurveySchema(surveySchemaWithSchemaPartIds);
      }

      const saveResult = await action;
      this.clearAndReturn(saveResult.value.id);
    } catch (e) {
      this.setState({ submitError: true });
    }
  }

  onSchemaStatusChange(event) {
    this.props.dispatchUpdate({ status: event.target.value });
  }

  isStatusSelectionDisabled(status) {
    const schema = this.props.surveySchema;
    if (status === publicationStatusValues.PUBLISHED) {
      return schema.surveySchemaParts.some((part) => part.status === publicationStatusValues.DRAFT);
    }
    return false;
  }

  clearAndReturn(surveySchemaId) {
    const returnRoute = surveySchemaId
      ? getCompiledPath(routes.surveySchemas.schemas.schema.index, { surveySchemaId })
      : routes.surveySchemas.schemas.index;

    this.props.dispatchClearSurveySchema();
    this.props.navigate(returnRoute);
  }

  render() {
    const { surveySchema, translate: t, params } = this.props;
    const {
      previewLang, createNew, schemaParts, extraSurveyPart, submitError, codeInvalid
    } = this.state;

    setDocumentTitle(t('common.preview'));

    const { surveySchemaId } = params;
    let entryReferer;
    if (createNew) {
      entryReferer = routes.surveySchemas.schemas.create.index;
    } else if (surveySchema.isNewVersion) {
      entryReferer = getCompiledPath(routes.surveySchemas.schemas.schema.newVersion.index, { surveySchemaId });
    } else if (surveySchema.isNewCopy) {
      entryReferer = getCompiledPath(routes.surveySchemas.schemas.schema.copy.index, { surveySchemaId });
    } else {
      entryReferer = getCompiledPath(routes.surveySchemas.schemas.schema.edit.index, { surveySchemaId });
    }

    return (
      <div className="page-container">
        <h1>{t('common.preview')}</h1>
        <PreviewLanguageSelector
          previewLang={previewLang}
          onLanguageSelect={this.onLanguageSelect}
        />
        <SurveySchemaPreviewTabs
          surveySchema={surveySchema}
          schemaParts={schemaParts}
          extraSurveyPart={extraSurveyPart}
          previewLang={previewLang}
          dimensionsTabRoute={DIMENSIONS_TAB_ROUTE}
        />

        <div className={styles.stateContainer}>
          <p>
            {this.state.createNew
              ? t('editor.surveySchema.preview.newSchemaStatusExplanation')
              : t('editor.surveySchema.preview.existingSchemaStatusExplanation')}
          </p>
          <div className={styles.stateInputs}>
            {[publicationStatusValues.DRAFT, publicationStatusValues.PUBLISHED].map((status) => (
              <div key={status} className={styles.stateInput}>
                <input
                  type="radio"
                  name="status"
                  id={`publicationStatus${status}`}
                  value={status}
                  checked={surveySchema.status === status}
                  onChange={this.onSchemaStatusChange}
                  disabled={this.isStatusSelectionDisabled(status)}
                />
                <label htmlFor={`publicationStatus${status}`}>{t(`status.${status}`)}</label>
              </div>
            ))}
          </div>
        </div>

        {codeInvalid && (
        <div className={`${editorStyles.errorContainer} ${editorStyles.error}`}>
          <p>{t('editor.identifierError')}</p>
        </div>
        )}

        {submitError && (
        <div className={`${editorStyles.errorContainer} ${editorStyles.error}`}>
          <p>{t('common.saveError')}</p>
        </div>
        )}

        <div className={editorStyles.navigation}>
          <ConfirmButton onConfirm={this.onCancel} modalText={t('confirmButton.confirmCancel')}>
            {t('common.cancel')}
          </ConfirmButton>
          <Link className="button--action" to={entryReferer}>
            {t('common.back')}
          </Link>
          <button
            type="button"
            className={`button button--action${(submitError || codeInvalid) ? ' disabled' : ''}`}
            onClick={this.onSave}
            disabled={submitError || codeInvalid}
            data-cy="schema-preview-save-button"
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    );
  }
}

const { func } = PropTypes;
SchemaPreview.propTypes = {
  navigate: func.isRequired,
  surveySchema: SurveySchemaEditorType.isRequired,
  dispatchClearSurveySchema: func.isRequired,
  dispatchUpdate: func.isRequired,
  dispatchPostSurveySchema: func.isRequired,
  dispatchPutSurveySchema: func.isRequired,
  dispatchUpdateFeedbackIntroduction: func.isRequired,
  dispatchPostNewSurveySchemaVersion: func.isRequired,
  dispatchCheckCode: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  ...langType
};

const mapStateToProps = ({ surveySchema }) => ({
  surveySchema: surveySchema.surveySchemaOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchClearSurveySchema: () =>
    dispatch(clearSurveySchemaOptionsAction()),
  dispatchUpdate: (options) =>
    dispatch(updateSurveySchemaOptionsAction(options)),
  dispatchPostSurveySchema: (surveySchemaOptions) =>
    dispatch(postSurveySchemaAction(surveySchemaOptions)),
  dispatchPutSurveySchema: (schemaId, surveySchemaOptions) =>
    dispatch(putSurveySchemaAction(schemaId, surveySchemaOptions)),
  dispatchUpdateFeedbackIntroduction: (schemaId, feedbackIntroduction) =>
    dispatch(updateSurveySchemaFeedbackIntroduction(schemaId, feedbackIntroduction)),
  dispatchPostNewSurveySchemaVersion: (schemaId, surveySchemaOptions) =>
    dispatch(postNewSurveySchemaVersionAction(schemaId, surveySchemaOptions)),
  dispatchCheckCode: (code) =>
    dispatch(getOrCheckSurveySchemaCodeAction(code)),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

export default withLang(withRouter(connect(mapStateToProps, mapDispatchToProps)(SchemaPreview)));
